import type { ReactNode } from 'react';
import { Paperclip, Printer, Wallet } from 'lucide-react';
import { Button, Modal, StatusBadge } from '@/components/ui';
import {
  type FactureDTO,
  MODE_PAIEMENT_FACTURE_LABEL,
  STATUT_PAIEMENT_LABEL,
  TIERS_TYPE_FACTURE_LABEL,
  TYPE_DOC_LABEL,
} from '@/services/achats';
import type { StatusKey } from '@/types';
import { formatDZD } from '@/utils/algeria';

const PAIE_UI: Record<string, StatusKey> = { SOLDEE: 'done', PARTIELLE: 'working', NON_PAYEE: 'stuck' };

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col">
      <dt className="text-xs uppercase tracking-wide text-content-muted">{label}</dt>
      <dd className="text-sm text-content-primary">{children || '—'}</dd>
    </div>
  );
}

/**
 * Fiche détaillée d'une facture fournisseur / sous-traitant : identité du tiers,
 * montants, statut de règlement et tranches de paiement (avec période G50),
 * pièces jointes. Bouton d'impression du PDF officiel.
 */
export function FactureDetailModal({
  open, onClose, facture, tiersNom, chantierNom, onPrint, onAddPaiement,
}: {
  open: boolean;
  onClose: () => void;
  facture: FactureDTO | null;
  tiersNom?: string | null;
  chantierNom?: string | null;
  onPrint: () => void;
  onAddPaiement?: () => void;
}) {
  const f = facture;
  const taux = f?.tva_exonere ? 'Exonéré' : `${((f?.tva_pct ?? 0) * 100).toFixed(0)} %`;
  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} title="Détail de la facture" size="xl"
      footer={<>
        <Button variant="secondary" onClick={onClose}>Fermer</Button>
        <Button onClick={onPrint}><Printer className="size-4" /> Imprimer</Button>
      </>}>
      {!f ? null : (
        <div className="space-y-5">
          {/* En-tête : n° + type doc + statuts */}
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-3">
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-content-primary">
                {f.numero_facture || <span className="italic text-content-muted">Sans n°</span>}
              </span>
              <span className="rounded bg-surface-hover px-2 py-0.5 text-[11px] font-semibold uppercase text-content-muted">
                {TYPE_DOC_LABEL[f.type_doc]}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <StatusBadge status={PAIE_UI[f.statut_paiement]} label={STATUT_PAIEMENT_LABEL[f.statut_paiement]} />
            </div>
          </div>

          {/* Identité & références */}
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-3">
            <Info label="Tiers">{tiersNom}</Info>
            <Info label="Nature">{f.tiers_type ? TIERS_TYPE_FACTURE_LABEL[f.tiers_type] : '—'}</Info>
            <Info label="Chantier">{chantierNom}</Info>
            <Info label="Date facture">{f.date_facture}</Info>
            <Info label="Échéance">{f.date_echeance}</Info>
            <Info label="Comptabilisée">{f.comptabilisee ? 'Oui' : 'Non'}</Info>
            <Info label="Validée le">{f.date_validation}</Info>
            <Info label="Référence interne">{f.reference}</Info>
          </dl>

          {/* Montants */}
          <div className="ml-auto w-full max-w-xs space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-content-secondary">Total HT</span><span className="tabular-nums font-medium">{formatDZD(f.montant_ht)}</span></div>
            <div className="flex justify-between"><span className="text-content-secondary">TVA ({taux})</span><span className="tabular-nums font-medium">{f.tva_exonere ? '—' : formatDZD(f.tva_montant)}</span></div>
            <div className="flex justify-between border-t border-border pt-1"><span className="font-bold text-content-primary">Total TTC</span><span className="tabular-nums font-bold text-brand-primary">{formatDZD(f.montant_ttc)}</span></div>
          </div>

          {/* Règlement */}
          <div className="rounded-lg border border-border bg-surface-secondary p-3">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm font-semibold text-content-primary">Règlements (rattachement G50 par période de déclaration)</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-content-muted">
                  Réglé <span className="font-semibold tabular-nums text-content-primary">{formatDZD(f.total_paye)}</span>
                  {' · '}Reste <span className="font-semibold tabular-nums text-content-primary">{formatDZD(f.reste_a_payer)}</span>
                </span>
                {onAddPaiement && f.statut_paiement !== 'SOLDEE' && (
                  <Button size="sm" onClick={onAddPaiement}><Wallet className="size-4" /> Payer la facture</Button>
                )}
              </div>
            </div>
            <div className="overflow-x-auto rounded-md border border-border bg-surface-primary">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase text-content-muted">
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2">G50</th>
                    <th className="px-3 py-2 text-right">Montant</th>
                    <th className="px-3 py-2 text-right">dont HT</th>
                    <th className="px-3 py-2 text-right">dont TVA</th>
                    <th className="px-3 py-2">Mode</th>
                    <th className="px-3 py-2">Référence</th>
                  </tr>
                </thead>
                <tbody>
                  {f.paiements.length === 0 ? (
                    <tr><td colSpan={7} className="px-3 py-3 text-center text-content-muted">Aucun règlement enregistré.</td></tr>
                  ) : f.paiements.map((p) => (
                    <tr key={p.id} className="border-b border-border/60 last:border-0">
                      <td className="px-3 py-1.5 tabular-nums">{p.date_paiement}</td>
                      <td className="px-3 py-1.5 tabular-nums text-content-muted">{String(p.periode_mois).padStart(2, '0')}/{p.periode_annee}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-medium">{formatDZD(p.montant_paye)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-content-secondary">{formatDZD(p.ht_paiement)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-content-secondary">{formatDZD(p.tva_paiement)}</td>
                      <td className="px-3 py-1.5">{p.mode_paiement ? MODE_PAIEMENT_FACTURE_LABEL[p.mode_paiement] : '—'}</td>
                      <td className="px-3 py-1.5 text-content-secondary">{p.paiement_reference || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pièces jointes */}
          {(f.pieces?.length ?? 0) > 0 && (
            <div>
              <p className="mb-1.5 text-xs uppercase tracking-wide text-content-muted">Pièces jointes</p>
              <div className="flex flex-wrap gap-2">
                {f.pieces.map((pj, i) => (
                  <a key={i} href={pj.url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface-secondary px-2.5 py-1 text-sm text-brand-primary hover:bg-surface-hover">
                    <Paperclip className="size-4" /> {pj.nom || `Pièce ${i + 1}`}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Observations */}
          {f.notes && (
            <div>
              <p className="mb-1 text-xs uppercase tracking-wide text-content-muted">Observations</p>
              <p className="whitespace-pre-wrap text-sm text-content-primary">{f.notes}</p>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
